import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarInset, SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { AppSidebar } from './sidebar/AppSidebar';
import { SidebarMobileTrigger } from './sidebar/SidebarMobileTrigger';
import { MobileChromeProvider } from './mobile/MobileChrome';
import { MobileViewportSync } from './mobile/MobileViewportSync';
import { TodayEventCountLoader, useTodayEventCountWanted } from './sidebar/use-nav-badges';

interface DashboardFrameProps {
  children: ReactNode;
  runtimeServices?: ReactNode;
  sidebarAddon?: ReactNode;
  userMenuAddon?: ReactNode;
  /** Phone navigation (the bottom tab bar). Without it phones get a small menu bar instead. */
  mobileNav?: ReactNode;
}

/**
 * Loads today's event count once a Calendar badge has shown (the sidebar's,
 * the drawer's or Work's Today tile). It lives here, on every dashboard
 * layout and at every width, so rotating across md doesn't load the count
 * again or blank the badges.
 */
function TodayEventCount() {
  return useTodayEventCountWanted() ? <TodayEventCountLoader /> : null;
}

/**
 * The phone drawer is a menu: it closes once any of its links has navigated,
 * and when the window grows past phone size (the sidebar takes over there),
 * so it doesn't pop back open when the window narrows again.
 */
function CloseDrawerOnNavigate() {
  const { key } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  useEffect(() => {
    setOpenMobile(false);
  }, [key, isMobile, setOpenMobile]);
  return null;
}

/**
 * Shared dashboard chrome. The authenticated layout supplies its live services.
 *
 * The frame is as tall as the visible screen (`--app-height`: 100dvh on
 * desktop, the area above the keyboard on touch screens) and clears the
 * notch in landscape. On phones it is a column: the page scrolls inside it
 * and the tab bar sits at the bottom. The page area starts below the status
 * bar and the compact call bar (`--call-bar-height`, set by the voice UI).
 * From 768px the page wrapper is `display: contents`, so the desktop layout
 * is unchanged; a phone in landscape shows the same call bar, so there the
 * sidebar and the page card move down below it. While the keyboard is up on
 * a short screen the card drops its top and bottom margins, which a ~160px
 * tall landscape view can't spare (the call bar slims to 28px there too, and
 * the measured `--call-bar-height` follows it).
 */
export function DashboardFrame({ children, runtimeServices, sidebarAddon, userMenuAddon, mobileNav }: DashboardFrameProps) {
  return (
    <MobileChromeProvider>
      <SidebarProvider className="h-app min-h-0 pl-safe pr-safe max-md:overflow-hidden md:pt-[var(--call-bar-height,0px)]">
        <MobileViewportSync />
        <CloseDrawerOnNavigate />
        <TodayEventCount />
        {runtimeServices}
        <AppSidebar
          addon={sidebarAddon}
          userMenuAddon={userMenuAddon}
          className="top-[var(--call-bar-height,0px)] h-[calc(100svh-var(--call-bar-height,0px))]"
        />
        <SidebarInset
          className={cn(
            'h-[calc(var(--app-height)-20px-var(--safe-bottom)-var(--call-bar-height,0px))] overflow-hidden max-md:h-full max-md:border-0',
            'kb-open:md:max-xl:[@media(max-height:560px)]:!my-0 kb-open:md:max-xl:[@media(max-height:560px)]:h-[calc(var(--app-height)-var(--call-bar-height,0px))]',
          )}
        >
          <div
            data-app-page
            className="flex min-h-0 flex-1 flex-col overflow-hidden pt-[calc(var(--safe-top)+var(--call-bar-height,0px))] md:contents"
          >
            {!mobileNav && <SidebarMobileTrigger />}
            {children}
          </div>
          {mobileNav}
        </SidebarInset>
      </SidebarProvider>
    </MobileChromeProvider>
  );
}
